/**
 * StartPageTab - Welcome/Dashboard screen component
 *
 * Main tab component for the StartPage workspace type.
 * Displays connection status, quick actions, recent tabs, and query history.
 */

import { useCallback, useMemo } from 'react';
import type { WorkspaceTabComponentProps } from '../../core/types/workspaceTypes';
import { isStartPageTab } from '../../core/types/workspaceTypes';
import { useWorkspaceStore } from '../../core/store/workspaceStore';
import { ConnectionStatusSection } from './ConnectionStatusSection';
import { RecentTabsSection } from './RecentTabsSection';
import { QueryHistorySection } from './QueryHistorySection';
import { WelcomeHeader } from './WelcomeHeader';
import { RecentScriptsSection } from './RecentScriptsSection';
import { CommandPaletteTrigger } from '../../../command-palette';
import { ReviewPromptCard } from '../../../review-prompt/components/ReviewPromptCard';
import { TrialStartCard } from '../../../../core/licensing/components/TrialStartCard';

export function StartPageTab({ tabId, className }: WorkspaceTabComponentProps) {
  const tab = useWorkspaceStore((state) => state.tabs.find((t) => t.id === tabId));
  const updateTabContent = useWorkspaceStore((state) => state.updateTabContent);

  // Extract content safely (must be before any returns for hook rules)
  const expandedSections = useMemo(() => {
    if (tab && isStartPageTab(tab)) {
      return tab.content.expandedSections ?? {};
    }
    return {};
  }, [tab]);

  const tabSearchQuery = useMemo(() => {
    if (tab && isStartPageTab(tab)) {
      return tab.content.tabSearchQuery ?? '';
    }
    return '';
  }, [tab]);

  const selectedConnectionId = useMemo(() => {
    if (tab && isStartPageTab(tab)) {
      return tab.content.selectedConnectionId ?? null;
    }
    return null;
  }, [tab]);

  const connectionsExpanded = expandedSections.connections ?? true;
  const recentTabsExpanded = expandedSections.recentTabs ?? false;
  const recentScriptsExpanded = expandedSections.recentScripts ?? false;
  const queryHistoryExpanded = expandedSections.queryHistory ?? false;

  // Handler to update section expanded state (must be before any returns)
  const handleConnectionsExpandedChange = useCallback(
    (expanded: boolean) => {
      updateTabContent(tabId, {
        expandedSections: {
          ...expandedSections,
          connections: expanded,
        },
      });
    },
    [tabId, updateTabContent, expandedSections]
  );

  const handleRecentTabsExpandedChange = useCallback(
    (expanded: boolean) => {
      updateTabContent(tabId, {
        expandedSections: {
          ...expandedSections,
          recentTabs: expanded,
        },
      });
    },
    [tabId, updateTabContent, expandedSections]
  );

  const handleRecentScriptsExpandedChange = useCallback(
    (expanded: boolean) => {
      updateTabContent(tabId, {
        expandedSections: {
          ...expandedSections,
          recentScripts: expanded,
        },
      });
    },
    [tabId, updateTabContent, expandedSections]
  );

  const handleSearchQueryChange = useCallback(
    (query: string) => {
      updateTabContent(tabId, {
        tabSearchQuery: query,
      });
    },
    [tabId, updateTabContent]
  );

  const handleQueryHistoryExpandedChange = useCallback(
    (expanded: boolean) => {
      updateTabContent(tabId, {
        expandedSections: {
          ...expandedSections,
          queryHistory: expanded,
        },
      });
    },
    [tabId, updateTabContent, expandedSections]
  );

  const handleSelectedConnectionChange = useCallback(
    (connectionId: string | null) => {
      updateTabContent(tabId, {
        selectedConnectionId: connectionId,
      });
    },
    [tabId, updateTabContent]
  );

  // Type guard to ensure we have a start page tab
  if (!tab || !isStartPageTab(tab)) {
    return (
      <div className={`flex h-full w-full items-center justify-center ${className ?? ''}`}>
        <p className="text-muted-foreground">Invalid tab type</p>
      </div>
    );
  }

  return (
    <div
      className={`flex h-full w-full flex-col overflow-auto bg-background ${className ?? ''}`}
      data-testid="start-page-tab"
    >
      {/* Main content area with max width and padding */}
      <div className="mx-auto w-full max-w-5xl px-6 pt-2 pb-8 space-y-6">
        {/* Welcome Header with branding */}
        <WelcomeHeader />

        {/* Review Prompt Card - renders only once the review-prompt state machine is armed */}
        <ReviewPromptCard />

        {/* Trial Start Card - passive day-1 trial/sign-in surface while the trial decision is open */}
        <TrialStartCard />

        {/* Command Palette search trigger */}
        <CommandPaletteTrigger />

        {/* Connections Section */}
        <ConnectionStatusSection
          isExpanded={connectionsExpanded}
          onExpandedChange={handleConnectionsExpandedChange}
        />

        {/* Recent Tabs Section */}
        <RecentTabsSection
          isExpanded={recentTabsExpanded}
          onExpandedChange={handleRecentTabsExpandedChange}
          searchQuery={tabSearchQuery}
          onSearchQueryChange={handleSearchQueryChange}
        />

        {/* Recent Scripts Section */}
        <RecentScriptsSection
          isExpanded={recentScriptsExpanded}
          onExpandedChange={handleRecentScriptsExpandedChange}
        />

        {/* Query History Section */}
        <QueryHistorySection
          isExpanded={queryHistoryExpanded}
          onExpandedChange={handleQueryHistoryExpandedChange}
          selectedConnectionId={selectedConnectionId}
          onSelectedConnectionChange={handleSelectedConnectionChange}
        />
      </div>
    </div>
  );
}
